import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { useUserAuth } from 'providers/userAuth'
import auth from 'utils/firebase'
import { buildPortalSignInUrl, getPortalLoginAction } from 'utils/portalLogin'

const ISSUER = process.env.NEXT_PUBLIC_PORTAL_ISSUER

// Federated-login init page (TKO-4522). The portal redirects unauthenticated theexpert users
// here. We ensure a Firebase-signed-in user, then auto-submit a top-level form POST (browser
// navigation, not XHR — the bridge replies with a 302 chain that must carry the browser onward)
// to the federation bridge.
//
// A signed-out user round-trips through /sign-in with a returnUrl back here (TKO-4755), so a
// portal-first landing completes federation instead of dead-ending. Any signed-in Firebase user
// submits; the federation bridge is the authority on who may actually federate.
const PortalLogin = () => {
  const router = useRouter()
  const { authRequestID } = router.query
  const { isPageLoading, user } = useUserAuth()
  const formRef = useRef<HTMLFormElement>(null)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const action = getPortalLoginAction({
    isReady: router.isReady,
    isPageLoading,
    isSignedIn: Boolean(user),
    authRequestID:
      typeof authRequestID === 'string' && authRequestID !== ''
        ? authRequestID
        : undefined,
  })

  useEffect(() => {
    if (action === 'redirectToSignIn') {
      router.replace(buildPortalSignInUrl(String(authRequestID)))
    }
    if (action === 'submit') {
      auth.currentUser
        ?.getIdToken()
        .then(setIdToken)
        .catch(() => setError('Could not read your Firebase session.'))
    }
  }, [action, router, authRequestID])

  useEffect(() => {
    if (idToken && formRef.current) {
      formRef.current.submit()
    }
  }, [idToken])

  if (!ISSUER) {
    return <p>Portal auth is not configured.</p>
  }
  if (error) {
    return <p>{error}</p>
  }
  if (action === 'missingAuthRequestID') {
    return <p>Missing authRequestID.</p>
  }
  if (action !== 'submit') {
    return <p>Loading…</p>
  }

  return (
    <form ref={formRef} method="POST" action={`${ISSUER}/federation/firebase`}>
      <input
        type="hidden"
        name="authRequestID"
        value={String(authRequestID ?? '')}
      />
      <input type="hidden" name="id_token" value={idToken ?? ''} />
      <noscript>
        <button type="submit">Continue</button>
      </noscript>
    </form>
  )
}

export default PortalLogin
